import { Timestamp } from 'firebase/firestore';

/** A single product entry saved on a shopping list (snapshot taken from a cart). */
export interface ShoppingListItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
}

/** Firestore document shape for `ShoppingLists/{listId}`. */
export interface ShoppingList {
  id: string;
  userId: string;
  name: string;
  supermarketId: string;
  supermarketName: string;
  notificationDate: Timestamp | null;
  items: ShoppingListItem[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** Payload used when creating or editing a shopping list. */
export interface ShoppingListWritePayload {
  userId: string;
  name: string;
  supermarketId: string;
  supermarketName: string;
  notificationDate: Timestamp | null;
  items: ShoppingListItem[];
}

/** Live stock information for one product at one supermarket (derived from `Inventory`). */
export interface ProductAvailability {
  inStock: boolean;
  availableQuantity: number;
  salePrice: number | null;
}

/** productId -> availability lookup for a single supermarket. */
export type InventoryAvailabilityMap = Record<string, ProductAvailability>;

/** Product row from a supermarket catalog (for adding to a shopping list). */
export interface SupermarketCatalogProduct {
  productId: string;
  name: string;
  price: number;
  category: string;
  availableQuantity: number;
}

export const SHOPPING_LIST_MAX_ITEMS = 200; // Keep in sync with firestore.rules ShoppingLists items.size() <= 200

export interface ShoppingListAvailabilitySummary {
  totalItems: number;
  availableItems: number;
  missingItems: number;
}

export interface AddListToCartResult {
  totalProducts: number;
  addedProducts: number;
  skippedProductNames: string[];
  reducedQuantityNames: string[];
  cartInstanceId: string | null;
}

/**
 * Minimal shape of a nested cart instance under `Carts/{userId}/{supermarketId}`
 * (see docs/FIRESTORE_SCHEMA.md; distinct from the deprecated `CartInstance` in
 * types/cart.ts). Extra fields (transactionId, deliveryOption, ...) are preserved
 * via the index signature. Timestamp fields hold serverTimestamp() sentinels
 * while writing.
 */
export interface NestedCartInstance {
  instanceId: string;
  products: ShoppingListItem[];
  status: string;
  supermarketName: string;
  createdAt?: unknown;
  lastUpdatedAt?: unknown;
  hasUnseenUpdate?: boolean;
  [extra: string]: unknown;
}
